/**
 * sessionStore.ts — 2004va
 *
 * Holds the active shoot session for the current creator.
 * Created when they tap "New Shoot" or enter The Room as a creator.
 * Persisted to localStorage so it survives a page refresh.
 * Cleared when they explicitly end a session or leave the event.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '@/lib/supabase'

export type ShootType =
  | 'event_editorial'
  | 'event_social'
  | 'fashion_product'
  | 'portrait'
  | 'documentary'
  | 'sport'
  | 'general'

export interface ShootSession {
  id:             string
  participantId:  string
  eventId:        string | null
  eventName:      string | null
  venueName:      string | null
  inferredType:   ShootType
  confirmedType:  ShootType
  sessionBrief:   string | null   // free text from creator — "opportunity shot of Gris"
  mediaCount:     number
  startedAt:      string
}

interface SessionState {
  session:      ShootSession | null
  setSession:   (s: ShootSession | null) => void
  incrementCount: () => void
  clearSession: () => void
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      session: null,

      setSession: (session) => set({ session }),

      incrementCount: () => {
        const s = get().session
        if (!s) return
        set({ session: { ...s, mediaCount: s.mediaCount + 1 } })
      },

      clearSession: () => set({ session: null }),
    }),
    {
      name: 'the-room-session',
      // Only persist the session object, not functions
      partialize: (state) => ({ session: state.session }),
    }
  )
)

// ── Infer shoot type from event tags and participant role ─────────────────────
export function inferShootType(
  eventTags: string[] = [],
  eventName = '',
  participantRole = 'creator'
): ShootType {
  const tags = eventTags.map(t => t.toLowerCase())
  const name = eventName.toLowerCase()

  if (tags.some(t => ['fashion', 'runway', 'style', 'designer', 'brand'].includes(t))) return 'fashion_product'
  if (tags.some(t => ['sport', 'race', 'sailing', 'swim', 'dive', 'athletics', 'competition'].includes(t))) return 'sport'
  if (tags.some(t => ['portrait', 'headshot', 'profile'].includes(t))) return 'portrait'
  if (tags.some(t => ['documentary', 'editorial', 'press', 'news'].includes(t))) return 'documentary'
  if (tags.some(t => ['club', 'clubbing', 'music', 'dj', 'festival', 'rave', 'party', 'ibiza'].includes(t))) return 'event_editorial'
  if (name.includes('ibiza') || name.includes('pacha') || name.includes('dc-10')) return 'event_editorial'

  if (participantRole === 'press') return 'documentary'

  return 'general'
}

export const SHOOT_TYPE_LABELS: Record<ShootType, string> = {
  event_editorial: 'Event / Editorial',
  event_social:    'Event / Social',
  fashion_product: 'Fashion / Product',
  portrait:        'Portrait',
  documentary:     'Documentary',
  sport:           'Sport',
  general:         'General',
}

// ── Create session in Supabase and return full record ─────────────────────────
export async function createShootSession(params: {
  participantId: string
  eventId:       string | null
  eventName:     string | null
  venueName:     string | null
  inferredType:  ShootType
  confirmedType: ShootType
  sessionBrief:  string | null
}): Promise<ShootSession> {
  const { data, error } = await supabase
    .from('shoot_sessions')
    .insert({
      participant_id:  params.participantId,
      event_id:        params.eventId,
      inferred_type:   params.inferredType,
      confirmed_type:  params.confirmedType,
      session_brief:   params.sessionBrief,
      media_count:     0,
    })
    .select('id, started_at')
    .single()

  if (error) throw new Error(`createShootSession: ${error.message}`)

  // Also update participant's active session + event
  await supabase
    .from('participants')
    .update({
      active_session_id: data.id,
      active_event_id:   params.eventId,
    })
    .eq('id', params.participantId)

  return {
    id:            data.id,
    participantId: params.participantId,
    eventId:       params.eventId,
    eventName:     params.eventName,
    venueName:     params.venueName,
    inferredType:  params.inferredType,
    confirmedType: params.confirmedType,
    sessionBrief:  params.sessionBrief,
    mediaCount:    0,
    startedAt:     data.started_at,
  }
}
